import type { Subscription } from '@/shared/api'

/**
 * Whether the permanent free mobile grant — rather than a purchase — is what
 * entitles this subscription right now.
 *
 * `has_free_mobile_grant` ("holds the grant at all") is the first term rather
 * than an inference from it: without it this asks "active with nothing behind
 * it", which is a different question that happens to have the same answer
 * today. Every active state the server can emit either sets a `provider` or
 * folds into `is_lifetime` (`is_edu` among them), so the shape alone is
 * currently unambiguous — but that invariant lives entirely in the server, and
 * a new entitlement source that forgets to set `provider` would silently
 * reclassify a paying customer as a grantee and offer them a second checkout.
 *
 * The remaining terms still matter: the grant is held through a purchase that
 * supersedes it, so `provider == null` is what distinguishes "the grant is
 * entitling you" from "you hold it while paying for something else".
 *
 * Use `has_free_mobile_grant` — not this — to gate a plan picker. This goes
 * false the moment a purchase entitles the user, which would put the Mobile
 * offer back in front of a grant holder.
 */
export const isEntitledByFreeGrant = (subscription: Subscription | null | undefined): boolean =>
  subscription?.is_active === true &&
  subscription.has_free_mobile_grant === true &&
  subscription.provider == null &&
  subscription.is_lifetime !== true
